/**
 * ✅ RESTAURANT SEPET İŞLEMLERİ HOOK'U
 * 
 * Bu hook, restoran modunda sepet işlemlerini yönetir:
 * - removeFromCartWithRestaurant: Sepetten ürün silme (SQL'den de siler)
 * - updateQuantityWithRestaurant: Ürün miktarını güncelleme (SQL'de de günceller)
 * - clearCartWithRestaurant: Sepeti temizleme (SQL'den de siler)
 * - handleIncreaseSelectedWithRestaurant: Seçili ürünü artırma
 * - handleDecreaseSelectedWithRestaurant: Seçili ürünü azaltma
 * - handleDeleteSelectedWithRestaurant: Seçili ürünü silme
 * - addWaiterAction: Garson işlemlerini kaydetme
 * 
 * QuickSaleScreen.tsx'den ayrıştırıldı - Ocak 2026
 */

import { useCallback } from 'react';
import type { CartItem } from '../../quickSale/types';

// Türkiye saatini ISO formatında döndüren helper fonksiyon
const getTurkeyTimeISO = (): string => {
  const now = new Date();
  const turkeyDateStr = now.toLocaleString('sv-SE', { 
    timeZone: 'Europe/Istanbul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }).replace(' ', 'T');
  
  const ms = now.getMilliseconds().toString().padStart(3, '0');
  return `${turkeyDateStr}.${ms}`;
};

// Hook parametreleri için tip tanımı
export interface UseRestaurantCartOperationsParams {
  isRestaurantMode: boolean;
  tableId: string | null;
  restaurantScreenId: number | null;
  companyId: string | null;
  cart: CartItem[];
  setCart: React.Dispatch<React.SetStateAction<CartItem[]>>;
  showSnackbar: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
  waiterName: string | null;
  focusSearchBar?: () => void;
  clearCart: () => void;
  selectedCartItemId: string | null;
  canDeletePrintedItems?: boolean;
  // Genel ürün silme yetkisi (buton seviyesinde de kontrol edilir; burada
  // savunma amaçlı yeniden kontrol edilir).
  canRemoveProduct?: boolean;
  // ✅ YENİ: Mutfağa YAZDIRILMAMIŞ ürünü silme yetkisi. canRemoveProduct
  // olmasa bile printedQuantity=0 olan satır bu yetkiyle silinebilir/azaltılabilir.
  canDeleteUnprintedItems?: boolean;
  // Adisyon iptal yetkisi: son urun silinebilmesi icin sart kosulur. Yoksa
  // silme engellenir; varsa silme atlanir ve `requestCloseTableForLastItemRemoval`
  // tetiklenerek mevcut adisyon iptal akisi (iptal nedeni modali → handleCloseTable)
  // devralir.
  canCloseTable?: boolean;
  // ✅ YENİ: Masa tamamen yazdırılmamışsa (hiçbir satırda printedQuantity>0 yok)
  // adisyon iptali bu yetkiyle de yapılabilir → son ürün silme akışında
  // canCloseTable alternatifi olarak kabul edilir.
  canCancelUnprintedReceipt?: boolean;
  requestCloseTableForLastItemRemoval?: () => void;
  // Yazdırılmış ürünün silinmesi/azaltılması anında otomatik tetiklenecek
  // iptal fişi yazdırma callback'i. `useRestaurantKitchenOperations` tarafından
  // sağlanır. Hata durumunda silme akışını bloke etmez.
  printCancellationForRemovedItem?: (params: {
    item: CartItem;
    cancelledQuantity: number;
    previousQuantity: number;
    currentQuantity: number;
  }) => Promise<void>;
}

// Hook dönüş tipi
export interface UseRestaurantCartOperationsReturn {
  removeFromCartWithRestaurant: (cartItemId: string, reason?: string) => void;
  updateQuantityWithRestaurant: (cartItemId: string, change: number) => void;
  clearCartWithRestaurant: () => void;
  handleIncreaseSelectedWithRestaurant: () => void;
  handleDecreaseSelectedWithRestaurant: () => void;
  handleDeleteSelectedWithRestaurant: () => void;
  addWaiterAction: (activeSaleId: string | number, actionType: string, details: any) => Promise<void>;
}

/**
 * Restaurant modu için sepet işlemleri hook'u
 */
export const useRestaurantCartOperations = ({
  isRestaurantMode,
  tableId,
  restaurantScreenId,
  companyId,
  cart,
  setCart,
  showSnackbar,
  waiterName,
  focusSearchBar,
  clearCart,
  selectedCartItemId,
  canDeletePrintedItems = false,
  canRemoveProduct = true,
  canDeleteUnprintedItems = false,
  canCloseTable = false,
  canCancelUnprintedReceipt = false,
  requestCloseTableForLastItemRemoval,
  printCancellationForRemovedItem,
}: UseRestaurantCartOperationsParams): UseRestaurantCartOperationsReturn => {
  
  /**
   * WaiterActionsJson güncelleme yardımcı fonksiyonu
   */
  // 🚀 BANT GENİŞLİĞİ: Eski akış her harekette TÜM satışı (Items dahil) çekip,
  // TÜM geçmişi pretty-print JSON olarak geri gönderiyordu (2 büyük istek).
  // Yeni akış: yalnız yeni hareket sunucuya gönderilir (birkaç yüz byte),
  // sunucu WaiterActionsJson'a kendisi ekler (kompakt serileştirme).
  const addWaiterAction = useCallback(async (activeSaleId: string | number, actionType: string, details: any) => {
    try {
      const { restaurantActiveSaleApi } = await import('../../../../services/restaurantApiService');
      
      const saleId = typeof activeSaleId === 'string' ? parseInt(activeSaleId) : activeSaleId;
      
      const newAction = {
        action: actionType,
        waiter: waiterName || 'Bilinmeyen',
        time: getTurkeyTimeISO(),
        details: details
      };
      
      await restaurantActiveSaleApi.appendWaiterAction(saleId, newAction);
      
      console.log('✅ [WAITER ACTION] İşlem kaydedildi (append):', actionType);
    } catch (error: any) {
      console.error('❌ [WAITER ACTION] İşlem kaydedilemedi:', {
        error: error,
        message: error?.message,
        response: error?.response?.data,
      });
      // Hata atma, sadece logla (işlemi durdurmasın)
    }
  }, [waiterName]);

  /**
   * Restaurant Modu - Sepetten Çıkar (SQL'den de sil)
   * @param cartItemId - Sepetteki benzersiz item ID (artık productId değil!)
   */
  const removeFromCartWithRestaurant = useCallback((cartItemId: string, reason?: string) => {
    console.log('┌══════════════════════════════════════════════════════════┐');
    console.log('║ 🗑️ removeFromCartWithRestaurant ÇAĞRILDI (HOOK)          ║');
    console.log('└══════════════════════════════════════════════════════════┘');
    console.log('📋 [HOOK DEBUG] cartItemId:', cartItemId);
    console.log('📋 [HOOK DEBUG] reason:', reason);
    console.log('📋 [HOOK DEBUG] isRestaurantMode:', isRestaurantMode);
    console.log('📋 [HOOK DEBUG] tableId:', tableId);
    console.log('📋 [HOOK DEBUG] restaurantScreenId:', restaurantScreenId);
    console.log('📋 [HOOK DEBUG] companyId:', companyId);
    console.log('📋 [HOOK DEBUG] canDeletePrintedItems:', canDeletePrintedItems);
    console.log('📋 [HOOK DEBUG] waiterName:', waiterName);
    console.log('📋 [HOOK DEBUG] cart.length:', cart.length);

    // Restaurant modundaysa SQL'den de sil
    if (isRestaurantMode && tableId && restaurantScreenId && companyId) {
      (async () => {
        try {
          const removedItem = cart.find(item => item.cartItemId === cartItemId);
          console.log('📋 [HOOK DEBUG] cart.find sonucu:', !!removedItem);
          
          if (!removedItem) {
            console.error('❌ [ÜRÜN SİLME] Sepette item bulunamadı:', cartItemId);
            console.error('❌ [HOOK DEBUG] Mevcut cart cartItemId\'ler:', cart.map(i => i.cartItemId));
            return;
          }

          console.log('📋 [HOOK DEBUG] Silinecek ürün detay:', JSON.stringify({
            cartItemId: removedItem.cartItemId,
            id: removedItem.id,
            name: removedItem.name,
            sqlItemId: removedItem.sqlItemId,
            quantity: removedItem.quantity,
            printedQuantity: removedItem.printedQuantity,
            price: removedItem.price,
          }));

          // Yazdırılmış ürün silme yetkisi kontrolü
          const isPrinted = (removedItem.printedQuantity || 0) > 0;
          // ✅ YENİ: Masada mutfağa yazdırılmış herhangi bir satır var mı?
          // (Yazdırılmamış adisyon iptal yetkisinin geçerliliği için.)
          const hasPrintedInCart = cart.some(i => (i.printedQuantity || 0) > 0);
          console.log('🔍 [HOOK SİLME YETKİ]', {
            product: removedItem.name,
            printedQuantity: removedItem.printedQuantity || 0,
            isPrinted,
            hasPrintedInCart,
            canDeletePrintedItems,
            canRemoveProduct,
            canDeleteUnprintedItems,
            willBlock: isPrinted && !canDeletePrintedItems,
          });
          if (isPrinted && !canDeletePrintedItems) {
            console.warn('⛔ [HOOK YETKİ] Yazdırılmış ürün silme ENGELLENDİ:', removedItem.name);
            showSnackbar('Yetkiniz yok! Lütfen yetkisi olan personel ile oturum açın.', 'error');
            return;
          }

          // ✅ YENİ: Yazdırılmamış ürün — canRemoveProduct VEYA canDeleteUnprintedItems yeterli.
          if (!isPrinted && !canRemoveProduct && !canDeleteUnprintedItems) {
            console.warn('⛔ [HOOK YETKİ] Yazdırılmamış ürün silme ENGELLENDİ (yetki yok):', removedItem.name);
            showSnackbar('Yetkiniz yok! Lütfen yetkisi olan personel ile oturum açın.', 'error');
            return;
          }

          // Son ürün özel kontrolü 1. aşama: yetki yoksa erken dön (yazdırma
          // yapmadan, çünkü silme zaten engellenecek).
          // ✅ YENİ: Masa tamamen yazdırılmamışsa canCancelUnprintedReceipt de
          // adisyon iptaline (son ürün silme akışı) izin verir.
          const canCancelViaUnprinted = canCancelUnprintedReceipt && !hasPrintedInCart;
          if (cart.length === 1 && !canCloseTable && !canCancelViaUnprinted) {
            console.warn('⛔ [HOOK YETKİ] Son ürün silme ENGELLENDİ (canCloseTable/canCancelUnprintedReceipt yok)');
            showSnackbar('Son ürünü silmek için adisyon kapatma yetkisi gereklidir.', 'error');
            return;
          }

          // ✅ YENİ: Yazdırılmış ürün siliniyorsa, mutfağa iptal fişi yazdır.
          // Hem son ürün hem normal silme için ortak. Hata olursa silme akışını
          // bloke etmez (helper kendi içinde catch eder, sessiz fail).
          // Kritik: SQL delete'ten ÖNCE çağrılmalı çünkü hard delete sonrası
          // `handlePrintToKitchen` cancellation tespit edemez.
          const printedQtyForCancel = removedItem.printedQuantity || 0;
          if (printedQtyForCancel > 0 && printCancellationForRemovedItem) {
            console.log('🖨️ [HOOK İPTAL FİŞİ] Mutfak yazıcısına iptal fişi gönderiliyor (silme öncesi)...', {
              product: removedItem.name,
              printedQty: printedQtyForCancel,
            });
            await printCancellationForRemovedItem({
              item: removedItem,
              cancelledQuantity: printedQtyForCancel,
              previousQuantity: printedQtyForCancel,
              currentQuantity: 0,
            });
          }

          // Son ürün özel kontrolü 2. aşama: yazdırma yapıldıktan sonra adisyon
          // iptal akışını tetikle. SQL silmeyi atla; iptal akışı (handleCloseTable)
          // ActiveSale'i CompletedSales'e UNPAID olarak taşıyacak.
          if (cart.length === 1 && requestCloseTableForLastItemRemoval) {
            console.log('🔄 [HOOK SON URUN] Adisyon iptal akışı tetikleniyor (silme atlandı, iptal fişi gönderildi)');
            requestCloseTableForLastItemRemoval();
            return;
          }
          console.log('✅ [HOOK DEBUG] Yetki kontrolü GEÇTİ, SQL silme başlıyor...');

          console.log('🍽️ [ÜRÜN SİLME] SQL\'den siliniyor...', { 
            cartItemId,
            sqlItemId: removedItem.sqlItemId,
            productName: removedItem.name
          });
          
          const { restaurantActiveSaleApi, restaurantActiveSaleItemApi } = await import('../../../../services/restaurantApiService');
          
          // Aktif satışı al
          console.log('📋 [HOOK DEBUG] getByTableId çağrılıyor:', parseInt(tableId));
          const activeSale = await restaurantActiveSaleApi.getByTableId(parseInt(tableId));
          console.log('📋 [HOOK DEBUG] activeSale:', activeSale ? { Id: activeSale.Id, TableId: activeSale.TableId } : null);
          if (!activeSale) {
            console.error('❌ [ÜRÜN SİLME] Aktif satış bulunamadı');
            return;
          }

          // SQL'den sil (sqlItemId kullan)
          if (removedItem.sqlItemId) {
            console.log('📋 [HOOK DEBUG] restaurantActiveSaleItemApi.delete çağrılıyor:', removedItem.sqlItemId);
            await restaurantActiveSaleItemApi.delete(removedItem.sqlItemId);
            console.log('✅ [ÜRÜN SİLME] SQL\'den silindi!', { sqlItemId: removedItem.sqlItemId });
            
            await addWaiterAction(activeSale.Id, 'remove_item', {
              productId: removedItem.id,
              productName: removedItem.name,
              quantity: removedItem.quantity,
              totalPrice: removedItem.price * removedItem.quantity,
              sqlItemId: removedItem.sqlItemId,
              ...(reason ? { reason } : {})
            });
          } else {
            console.warn('⚠️ [HOOK DEBUG] sqlItemId YOK! SQL silme atlanıyor.');
          }
          
          // Local sepetten de sil (cartItemId ile)
          setCart(prevCart => {
            const before = prevCart.length;
            const after = prevCart.filter(item => item.cartItemId !== cartItemId);
            console.log(`📋 [HOOK DEBUG] setCart: ${before} → ${after.length} (${before - after.length} silindi)`);
            return after;
          });
          console.log('✅ [ÜRÜN SİLME] Local sepetten silindi!');
          
          // İşlem bitince fokusla (varsa)
          focusSearchBar?.();
        } catch (error: any) {
          console.error('❌ [ÜRÜN SİLME] SQL hatası:', error);
          console.error('❌ [HOOK DEBUG] Hata detay:', {
            message: error?.message,
            response: error?.response?.data,
            status: error?.response?.status,
          });
          showSnackbar('Ürün SQL\'den silinemedi!', 'error');
        }
      })();
    } else {
      console.log('📋 [HOOK DEBUG] Restaurant modu DEĞİL, direkt sepetten siliniyor');
      // Restaurant modu değilse direkt sepetten çıkar
      setCart(prevCart => prevCart.filter(item => item.cartItemId !== cartItemId));
      
      // İşlem bitince fokusla (varsa)
      focusSearchBar?.();
    }
  }, [isRestaurantMode, tableId, restaurantScreenId, companyId, cart, showSnackbar, addWaiterAction, setCart, focusSearchBar, canDeletePrintedItems, canRemoveProduct, canDeleteUnprintedItems, canCloseTable, canCancelUnprintedReceipt, requestCloseTableForLastItemRemoval, printCancellationForRemovedItem, waiterName]);

  /**
   * Restaurant Modu - Miktar Güncelle (SQL'de de güncelle)
   */
  const updateQuantityWithRestaurant = useCallback((cartItemId: string, change: number) => {
    // Restaurant modundaysa SQL'de de güncelle
    if (isRestaurantMode && tableId && restaurantScreenId && companyId) {
      (async () => {
        try {
          const item = cart.find(i => i.cartItemId === cartItemId);
          if (!item) {
            console.error('❌ [MİKTAR DEĞİŞTİR] Sepette item bulunamadı:', cartItemId);
            return;
          }

          const newQuantity = item.quantity + change;
          const printedQty = item.printedQuantity || 0;
          
          // Azaltma yapılıyorsa ve yazdırılmış miktarın altına düşüyorsa yetki kontrolü
          console.log('🔍 [HOOK AZALT YETKİ]', {
            product: item.name,
            currentQty: item.quantity,
            newQuantity,
            printedQty,
            canDeletePrintedItems,
            willBlock: change < 0 && printedQty > 0 && newQuantity < printedQty && !canDeletePrintedItems,
          });
          if (change < 0 && printedQty > 0 && newQuantity < printedQty && !canDeletePrintedItems) {
            console.warn('⛔ [HOOK YETKİ] Yazdırılmış ürün azaltma ENGELLENDİ:', item.name);
            showSnackbar('Yetkiniz yok! Lütfen yetkisi olan personel ile oturum açın.', 'error');
            return;
          }
          
          // Miktar 0 veya negatif olacaksa sil
          if (newQuantity <= 0) {
            removeFromCartWithRestaurant(cartItemId);
            return;
          }

          // ✅ KRİTİK: Yazdırılmış miktar (PrintedQuantity) yeni miktarı GEÇEMEZ.
          // Miktar yazdırılan adedin altına inerse, mutfaktan iptal fişiyle düşen
          // adet kadar PrintedQuantity de azaltılmalı. Aksi halde aşağıdaki gibi
          // bozulma olur: x2 yazdır → 1 azalt (iptal 1) → kalan 1'i sil → iptal 2
          // (çünkü printedQuantity hâlâ 2 kalmış). min(printedQty, newQuantity)
          // ile her durumda senkron tutulur (artışta değişmez).
          const newPrintedQty = Math.min(printedQty, newQuantity);

          console.log('🍽️ [MİKTAR DEĞİŞTİR] SQL\'de güncelleniyor...', { 
            cartItemId,
            sqlItemId: item.sqlItemId,
            productName: item.name,
            oldQuantity: item.quantity,
            newQuantity,
            oldPrintedQty: printedQty,
            newPrintedQty,
          });
          
          const { restaurantActiveSaleApi, restaurantActiveSaleItemApi } = await import('../../../../services/restaurantApiService');
          
          // Aktif satışı al
          const activeSale = await restaurantActiveSaleApi.getByTableId(parseInt(tableId));
          if (!activeSale) {
            console.error('❌ [MİKTAR DEĞİŞTİR] Aktif satış bulunamadı');
            return;
          }

          // SQL'de güncelle (sqlItemId kullan) — PrintedQuantity de senkronlanır
          if (item.sqlItemId) {
            await restaurantActiveSaleItemApi.update(item.sqlItemId, {
              Quantity: newQuantity,
              TotalPrice: newQuantity * item.price,
              PrintedQuantity: newPrintedQty,
            });
            console.log('✅ [MİKTAR DEĞİŞTİR] SQL\'de güncellendi!', { sqlItemId: item.sqlItemId, newPrintedQty });
            
            // Garson işlemini kaydet
            await addWaiterAction(activeSale.Id, 'update_quantity', {
              productId: item.id,
              productName: item.name,
              oldQuantity: item.quantity,
              newQuantity: newQuantity,
              change: change,
              sqlItemId: item.sqlItemId
            });
          }

          // ✅ YENİ: Yazdırılmış miktar altına iniyorsak otomatik iptal fişi gönder.
          // newQuantity > 0 koşulu: 0'a inişte (removeFromCart'a delege ediliyor)
          // çift yazdırma olmaması için. removeFromCart kendisi iptal fişi yazdırıyor.
          // change < 0 + newQuantity < printedQty + newQuantity > 0:
          //   cancelledQty = printedQty - newQuantity (mutfakta artık olmaması gereken miktar)
          if (change < 0 && newQuantity > 0 && printedQty > 0 && newQuantity < printedQty && printCancellationForRemovedItem) {
            const cancelledQty = printedQty - newQuantity;
            console.log('🖨️ [HOOK İPTAL FİŞİ] Miktar azaltıldı, mutfak yazıcısına iptal fişi gönderiliyor...', {
              product: item.name,
              oldQty: item.quantity,
              newQty: newQuantity,
              printedQty,
              cancelledQty,
            });
            await printCancellationForRemovedItem({
              item,
              cancelledQuantity: cancelledQty,
              previousQuantity: printedQty,
              currentQuantity: newQuantity,
            });
          }

          // Local sepette de güncelle (cartItemId ile) — printedQuantity senkron
          setCart(prevCart => prevCart.map(cartItem => 
            cartItem.cartItemId === cartItemId 
              ? { ...cartItem, quantity: newQuantity, printedQuantity: newPrintedQty }
              : cartItem
          ));
          console.log('✅ [MİKTAR DEĞİŞTİR] Local sepette güncellendi!');
          
          // İşlem bitince fokusla (varsa)
          focusSearchBar?.();
        } catch (error) {
          console.error('❌ [MİKTAR DEĞİŞTİR] SQL hatası:', error);
          showSnackbar('Miktar SQL\'de güncellenemedi!', 'error');
        }
      })();
    } else {
      // Restaurant modu değilse direkt güncelle (cartItemId ile)
      setCart(prevCart => prevCart.map(cartItem => 
        cartItem.cartItemId === cartItemId 
          ? { ...cartItem, quantity: Math.max(1, cartItem.quantity + change) }
          : cartItem
      ));
      
      // İşlem bitince fokusla (varsa)
      focusSearchBar?.();
    }
  }, [isRestaurantMode, tableId, restaurantScreenId, companyId, cart, showSnackbar, addWaiterAction, removeFromCartWithRestaurant, setCart, focusSearchBar, canDeletePrintedItems, printCancellationForRemovedItem]);

  /**
   * Restaurant Modu - Sepeti Temizle (SQL'den de sil)
   */
  const clearCartWithRestaurant = useCallback(() => {
    // Restaurant modundaysa SQL'den de sil
    if (isRestaurantMode && tableId && restaurantScreenId && companyId) {
      (async () => {
        try {
          console.log('🍽️ [SEPET TEMİZLE] SQL\'den tüm ürünler siliniyor...', { 
            tableId,
            itemCount: cart.length
          });
          
          const { restaurantActiveSaleApi, restaurantActiveSaleItemApi } = await import('../../../../services/restaurantApiService');
          
          // Aktif satışı al
          const activeSale = await restaurantActiveSaleApi.getByTableId(parseInt(tableId));
          if (!activeSale) {
            console.error('❌ [SEPET TEMİZLE] Aktif satış bulunamadı');
            clearCart(); // Local'i temizle
            return;
          }

          // Garson işlemini kaydet (silmeden önce)
          await addWaiterAction(activeSale.Id, 'clear_cart', {
            itemCount: cart.length,
            items: cart.map(item => ({
              productId: item.id,
              productName: item.name,
              quantity: item.quantity
            }))
          });

          // Tüm ürünleri sil
          const items = await restaurantActiveSaleItemApi.getByActiveSaleId(activeSale.Id);
          for (const item of items) {
            await restaurantActiveSaleItemApi.delete(item.Id);
          }
          
          console.log('✅ [SEPET TEMİZLE] SQL\'den tüm ürünler silindi!');
          
          // Local sepeti de temizle
          clearCart();
          
          // İşlem bitince fokusla (varsa)
          focusSearchBar?.();
        } catch (error) {
          console.error('❌ [SEPET TEMİZLE] SQL hatası:', error);
          showSnackbar('Sepet SQL\'den temizlenemedi!', 'error');
        }
      })();
    } else {
      // Restaurant modu değilse direkt temizle
      clearCart();
      
      // İşlem bitince fokusla (varsa)
      focusSearchBar?.();
    }
  }, [clearCart, isRestaurantMode, tableId, restaurantScreenId, companyId, cart, showSnackbar, addWaiterAction, focusSearchBar]);

  /**
   * Restaurant Modu için Artır butonu
   */
  const handleIncreaseSelectedWithRestaurant = useCallback(() => {
    console.log('🔵 [RESTAURANT] Artır butonu tıklandı', { selectedCartItemId });
    if (selectedCartItemId) {
      updateQuantityWithRestaurant(selectedCartItemId, 1);
    }
  }, [selectedCartItemId, updateQuantityWithRestaurant]);

  /**
   * Restaurant Modu için Azalt butonu
   */
  const handleDecreaseSelectedWithRestaurant = useCallback(() => {
    console.log('🔵 [RESTAURANT] Azalt butonu tıklandı', { selectedCartItemId });
    if (selectedCartItemId) {
      updateQuantityWithRestaurant(selectedCartItemId, -1);
    }
  }, [selectedCartItemId, updateQuantityWithRestaurant]);

  /**
   * Restaurant Modu için Sil butonu
   */
  const handleDeleteSelectedWithRestaurant = useCallback(() => {
    console.log('🔵 [RESTAURANT] Sil butonu tıklandı', { selectedCartItemId });
    if (selectedCartItemId) {
      removeFromCartWithRestaurant(selectedCartItemId);
    }
  }, [selectedCartItemId, removeFromCartWithRestaurant]);

  return {
    removeFromCartWithRestaurant,
    updateQuantityWithRestaurant,
    clearCartWithRestaurant,
    handleIncreaseSelectedWithRestaurant,
    handleDecreaseSelectedWithRestaurant,
    handleDeleteSelectedWithRestaurant,
    addWaiterAction,
  };
};

export default useRestaurantCartOperations;
